import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";
import CustomError from "@components/Error/CustomError";
import { MetaTagDescriptor } from "@components/MetaTags/MetaTags";
import { Route } from "@route-types/SingaporeGe/+types/SingaporeGePage";
import { fetchSingaporeGePageData } from "@routes/SingaporeGe/server/SingaporeGePage.server";
import { getSingaporeGeData } from "@routes/SingaporeGe/utils/helpers";
import { SingaporeGePageContext } from "@routes/SingaporeGe/utils/types";
import { fetchContext, ResponseType } from "@sphtech/web2-core/ssr";
import { getEnvironment } from "@util/constant";
import { getEnvironmentDomain } from "@util/helpers";
import { HttpStatusCode } from "axios";

import SingaporeGePageLayoutFactory from "./SingaporeGePageLayoutFactory";

/** This is the main entry point component for /keywords/ge2025 route. */
const SingaporeGePage = (): React.ReactElement => {
  const context = useLoaderData<SingaporeGePageContext>();

  // StatusCode is not 200
  if (context && context.statusCode !== HttpStatusCode.Ok) {
    return <CustomError statusCode={context.statusCode} />;
  }

  // Check if payload is missing
  if (typeof context?.payload === "undefined") {
    return <CustomError statusCode={HttpStatusCode.NotFound} />;
  }

  const data = getSingaporeGeData(context.payload.data);

  if (!data) {
    return <CustomError statusCode={HttpStatusCode.NotFound} />;
  }

  return (
    <SingaporeGePageLayoutFactory
      data={data}
      gaProps={context.payload.gaProps}
    />
  );
};

export default SingaporeGePage;

export const loader = async ({
  request,
}: Route.LoaderArgs): Promise<SingaporeGePageContext> => {
  const url = new URL(request.url, `http://localhost`);
  const path = url.pathname;
  const envDomain = getEnvironmentDomain(getEnvironment());
  const absolutePath = `${envDomain}${path}`;

  return await fetchSingaporeGePageData(absolutePath);
};

export const meta = ({ loaderData }: Route.MetaArgs): MetaTagDescriptor[] => {
  const context = loaderData as SingaporeGePageContext;

  if (context.type !== ResponseType.SUCCESS) return [];

  const data = context.payload;

  if (!data) return [];
  if (!data.metaTags) return [];

  return data.metaTags;
};

export const clientLoader = async (args: ClientLoaderFunctionArgs) => {
  const result = (await fetchContext(args)) as {
    context?: SingaporeGePageContext;
  };

  if (!result.context) return await args.serverLoader();

  return result.context;
};
